import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { Body, Muted } from "../../components/Typography";
import { ApiError, api } from "../../lib/api";
import type { PaymentMethodResponse, PlanResponse, StoreResponse } from "../../lib/api";
import { type ReferralCodeChip, ReferralCodesField } from "./ReferralCodesField";
import styles from "./SetupWizard.module.css";
import {
  BADGES,
  CONTACT_URL,
  type PrepayTerm,
  coveringPlan,
  dollars,
  extraCameras,
  fitsPlan,
  monthlyCents,
  prepayPercent,
  recommendedPlan,
  termCents,
  termSavingsCents,
} from "./plans";

// PRD §4 copy requirement: the literal no-charge-until date, concrete, locale-formatted
// like the rest of the app's dates. The trial length comes from the server (one constant,
// `lifecycle.TRIAL_DAYS`), never from the browser.
export const noChargeUntilDate = (trialDays: number, from: Date = new Date()): string =>
  new Date(from.getTime() + trialDays * 24 * 60 * 60 * 1000).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

type Phase =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "unconfigured" }
  | { kind: "complimentary" }
  | {
      kind: "form";
      publishableKey: string;
      clientSecret: string;
      pendingBilling: boolean;
      existingCard: PaymentMethodResponse | null;
      plans: PlanResponse[];
      trialDays: number;
      cameras: number;
    };

// A card vaulted before this step — from the Shopping page's inline capture (a device
// order needs a card, and the wizard's billing step used to be unreachable without the
// device) — is reused here instead of re-typed. Degrades to the type-a-card path when
// the wallet can't be read.
const vaultedPrimaryCard = async (storeId: string): Promise<PaymentMethodResponse | null> => {
  try {
    const methods = await api.listPaymentMethods(storeId);
    return methods.find((m) => m.is_primary) ?? methods[0] ?? null;
  } catch {
    return null;
  }
};

type BillingStepProps = {
  store: StoreResponse;
  /** Resume path for a store that predates billing (absent subscription row): activation
   * is offered, not forced — §7's absent-row safety default. */
  allowSkip: boolean;
  onComplete: () => void;
};

export const BillingStep = ({ store, allowSkip, onComplete }: BillingStepProps) => {
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const load = useCallback(async (): Promise<void> => {
    setPhase({ kind: "loading" });
    try {
      const config = await api.getBillingConfig();
      if (!config.publishable_key) {
        setPhase({ kind: "unconfigured" });
        return;
      }
      const summary = await api.getBillingSummary(store.id);
      const status = summary.subscription?.status;
      if (status === "complimentary") {
        setPhase({ kind: "complimentary" });
        return;
      }
      if (status && status !== "pending_billing") {
        // Already activated (trialing/active — or paused/canceled, which the Billing tab
        // owns): nothing for the wizard to collect. A pending_billing store is born dark
        // (operator ruling 2026-08-25) — this step IS its ignition, so it falls through
        // to the form.
        onCompleteRef.current();
        return;
      }
      const [intent, offer] = await Promise.all([
        api.createBillingSetupIntent(store.id),
        api.getBillingPlans(),
      ]);
      setPhase({
        kind: "form",
        publishableKey: config.publishable_key,
        clientSecret: intent.client_secret,
        pendingBilling: status === "pending_billing",
        existingCard: await vaultedPrimaryCard(store.id),
        plans: offer.plans,
        trialDays: offer.trial_days,
        cameras: summary.billable_cameras,
      });
    } catch (e) {
      setPhase({
        kind: "error",
        message: e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't load billing.",
      });
    }
  }, [store.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const publishableKey = phase.kind === "form" ? phase.publishableKey : null;
  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey],
  );

  if (phase.kind === "loading") {
    return (
      <Body>
        <Muted>Loading billing…</Muted>
      </Body>
    );
  }

  if (phase.kind === "error") {
    return (
      <div className={styles.form}>
        <div className={styles.banner}>
          <div>
            <div className={styles.bannerTitle}>Couldn't load billing</div>
            <div className={styles.bannerBody}>{phase.message}</div>
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.footerLeft} />
          <Button type="button" onClick={() => void load()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (phase.kind === "unconfigured") {
    return (
      <div className={styles.form}>
        <div className={styles.banner}>
          <div>
            <div className={styles.bannerTitle}>Billing isn't configured yet</div>
            <div className={styles.bannerBody}>
              This deployment has no payment processor set up, so plan activation isn't available
              right now. Continue with setup — your store runs normally, and billing can be
              activated later.
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.footerLeft} />
          <Button type="button" onClick={onComplete}>
            Continue →
          </Button>
        </div>
      </div>
    );
  }

  if (phase.kind === "complimentary") {
    return (
      <div className={styles.form}>
        <div className={styles.pilotNotice}>
          <div className={styles.bannerTitle}>Pilot — complimentary service</div>
          <div className={styles.bannerBody}>
            This store runs on a complimentary pilot plan. No payment information is collected, and
            you will never be billed.
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.footerLeft} />
          <Button type="button" onClick={onComplete}>
            Continue →
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret: phase.clientSecret }}>
      <ActivationForm
        store={store}
        allowSkip={allowSkip}
        pendingBilling={phase.pendingBilling}
        existingCard={phase.existingCard}
        plans={phase.plans}
        trialDays={phase.trialDays}
        cameras={phase.cameras}
        onComplete={onComplete}
      />
    </Elements>
  );
};

const ActivationForm = ({
  store,
  allowSkip,
  pendingBilling,
  existingCard,
  plans,
  trialDays,
  cameras,
  onComplete,
}: BillingStepProps & {
  pendingBilling: boolean;
  existingCard: PaymentMethodResponse | null;
  plans: PlanResponse[];
  trialDays: number;
  cameras: number;
}) => {
  const stripe = useStripe();
  const elements = useElements();

  // A card already in the Store Wallet (vaulted from Shopping's inline capture) is
  // reused as-is; "Use a different card" falls back to typing one.
  const [reusedCard, setReusedCard] = useState(existingCard);

  const offered = plans.filter((p) => p.sellable);
  const custom = plans.find((p) => !p.sellable) ?? null;
  const [planCode, setPlanCode] = useState<string | null>(
    () => recommendedPlan(plans, cameras)?.code ?? null,
  );
  const plan = offered.find((p) => p.code === planCode) ?? null;
  const [prepayTerm, setPrepayTerm] = useState<PrepayTerm>(1);
  const pickPlan = (next: PlanResponse): void => {
    setPlanCode(next.code);
    if (!next.prepayable) setPrepayTerm(1);
  };
  const [coupon, setCoupon] = useState("");
  const [codeChips, setCodeChips] = useState<ReferralCodeChip[]>([]);
  const [codesPending, setCodesPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // A confirmSetup that succeeded but whose activation failed (e.g. a dead coupon) keeps
  // the vaulted payment method, so the retry re-runs activation only — no re-typed card.
  const [vaultedPmId, setVaultedPmId] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (codesPending || submitting || plan === null) return;
    setError(null);
    setSubmitting(true);
    try {
      let pmId = reusedCard?.stripe_pm_id ?? vaultedPmId;
      if (pmId === null) {
        if (!stripe || !elements) return;
        const result = await stripe.confirmSetup({ elements, redirect: "if_required" });
        if (result.error) {
          setError(result.error.message ?? "Your card couldn't be confirmed. Try again.");
          return;
        }
        const pm = result.setupIntent.payment_method;
        pmId = typeof pm === "string" ? pm : (pm?.id ?? null);
        if (pmId === null) {
          setError("Card confirmation didn't return a payment method. Try again.");
          return;
        }
        setVaultedPmId(pmId);
      }
      const storeChip = codeChips.find((c) => c.kind === "store");
      const partnerChip = codeChips.find((c) => c.kind === "partner");
      await api.activateSubscription(store.id, {
        plan_code: plan.code,
        stripe_pm_id: pmId,
        prepay_term: prepayTerm,
        ...(coupon.trim() ? { coupon_code: coupon.trim() } : {}),
        ...(storeChip ? { sales_code: storeChip.code } : {}),
        ...(partnerChip ? { partner_code: partnerChip.code } : {}),
      });
      onComplete();
    } catch (e) {
      setError(
        e instanceof ApiError
          ? (e.body?.error ?? e.message)
          : "Couldn't activate the subscription.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      {pendingBilling && (
        <p className={styles.trialNote}>
          Your cameras are installed but not recording yet — completing this step starts your
          service.
        </p>
      )}
      {error && (
        <div className={styles.banner}>
          <div>
            <div className={styles.bannerTitle}>Couldn't complete activation</div>
            <div className={styles.bannerBody}>{error}</div>
          </div>
        </div>
      )}

      <div className={styles.field}>
        <span className={styles.label}>Plan</span>
        <div className={styles.planGrid}>
          {offered.map((p) => {
            const fits = fitsPlan(p, cameras);
            const selected = p.code === planCode;
            return (
              <button
                key={p.code}
                type="button"
                className={styles.planCard}
                data-state={!fits ? "disabled" : selected ? "selected" : "idle"}
                disabled={!fits || submitting}
                aria-pressed={selected}
                onClick={() => pickPlan(p)}
              >
                <span className={styles.planName}>{p.name}</span>
                <span className={styles.planPrice}>
                  {dollars(p.monthly_price_cents)}
                  <span className={styles.planPriceUnit}>/mo</span>
                </span>
                <span className={styles.planBlurb}>
                  {p.camera_overage
                    ? `${p.included_cameras} camera${p.included_cameras === 1 ? "" : "s"} included, then ${dollars(p.overage_cents_per_camera)}/mo per extra camera`
                    : `Up to ${p.included_cameras} cameras`}
                </span>
                <span className={styles.planBlurb}>
                  {p.max_users} member login{p.max_users === 1 ? "" : "s"} · up to{" "}
                  {p.max_watch_items} store-wide watch rules + {p.max_watch_items_per_camera} per
                  camera
                </span>
                {!fits && (
                  <span className={styles.planBlurb}>
                    You have {cameras} cameras — this plan stops at {p.included_cameras}.
                  </span>
                )}
                {BADGES[p.code] && <span className={styles.planBadge}>{BADGES[p.code]}</span>}
              </button>
            );
          })}
          {custom && (
            <a
              className={styles.planCard}
              data-state="idle"
              href={CONTACT_URL}
              target="_blank"
              rel="noreferrer"
            >
              <span className={styles.planName}>{custom.name} setup</span>
              <span className={styles.planPrice}>Quoted</span>
              <span className={styles.planBlurb}>
                Everything in Pro, for a camera count that doesn't fit a fixed plan. We'll quote it
                on what your store needs.
              </span>
              <span className={styles.planBlurb}>Contact us for custom setup →</span>
            </a>
          )}
        </div>
        <p className={styles.help}>
          Every plan includes 24/7 monitoring on your existing cameras, plain-English watch rules,
          agent-verified alerts by SMS and Telegram, live view and the activity timeline. Your price
          never goes up while you stay subscribed.
        </p>
        {plan && cameras > 0 && extraCameras(plan, cameras) > 0 && (
          <p className={styles.help}>
            Your {cameras} cameras on {plan.name}: {plan.included_cameras} included, the other{" "}
            {extraCameras(plan, cameras)} at {dollars(plan.overage_cents_per_camera)} each —{" "}
            {dollars(monthlyCents(plan, cameras))}/mo.
            {(() => {
              const covering = coveringPlan(plans, cameras);
              return covering && covering.code !== plan.code
                ? ` ${covering.name} covers all ${cameras} for ${dollars(monthlyCents(covering, cameras))}/mo.`
                : "";
            })()}
          </p>
        )}
      </div>

      {plan?.prepayable && (
        <div className={styles.field}>
          <label htmlFor="billing-term" className={styles.label}>
            Billing term
          </label>
          <Select
            id="billing-term"
            value={String(prepayTerm)}
            onChange={(e) => setPrepayTerm(Number(e.target.value) as PrepayTerm)}
            disabled={submitting}
          >
            <option value="1">
              Monthly — {dollars(monthlyCents(plan, cameras))}/mo, pay as you go
            </option>
            <option value="12">
              12 months upfront — {dollars(termCents(plan, cameras, 12))} (
              {dollars(Math.floor(termCents(plan, cameras, 12) / 12))}/mo), save{" "}
              {dollars(termSavingsCents(plan, cameras, 12))}
            </option>
          </Select>
          <p className={styles.help}>
            Prepay 12 months and save {prepayPercent(plan, 12)}%. Starts after your free trial.
          </p>
        </div>
      )}

      <div className={styles.grid2}>
        <div className={styles.field}>
          <label htmlFor="billing-coupon" className={styles.label}>
            Coupon code
          </label>
          <Input
            id="billing-coupon"
            value={coupon}
            onChange={(e) => setCoupon(e.target.value)}
            disabled={submitting}
            placeholder="Optional"
          />
          <p className={styles.help}>Checked when you activate.</p>
        </div>
        <ReferralCodesField
          chips={codeChips}
          onChange={setCodeChips}
          onPendingChange={setCodesPending}
          disabled={submitting}
        />
      </div>

      <div className={styles.field}>
        <span className={styles.label}>Payment card</span>
        {reusedCard ? (
          <>
            <p className={styles.help}>
              This store already has{" "}
              {reusedCard.last4 ? `a saved card ending in ${reusedCard.last4}` : "a saved card"} —
              starting your trial will use it, no need to re-enter it.
            </p>
            <div>
              <button
                type="button"
                onClick={() => setReusedCard(null)}
                disabled={submitting}
                className={styles.textBtn}
              >
                Use a different card
              </button>
            </div>
          </>
        ) : (
          <>
            <PaymentElement />
            <p className={styles.help}>
              A card is required to start service. Your first {trialDays} days are free — cancel in
              the trial from Settings → Billing and pay nothing.
            </p>
          </>
        )}
      </div>

      <p className={styles.trialNote}>
        You will not be charged until {noChargeUntilDate(trialDays)}.
        {plan
          ? ` Then ${dollars(termCents(plan, cameras, prepayTerm))}${prepayTerm === 1 ? " a month" : ` for ${prepayTerm} months`}.`
          : ""}
      </p>

      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          {allowSkip && (
            <button
              type="button"
              onClick={onComplete}
              disabled={submitting}
              className={styles.textBtn}
            >
              Skip for now
            </button>
          )}
        </div>
        <Button
          type="submit"
          disabled={submitting || codesPending || plan === null || !stripe || !elements}
        >
          {submitting ? "Activating…" : "Start free trial →"}
        </Button>
      </div>
    </form>
  );
};
